import {
  getService,
  NewRelicPageActionService,
} from '@seismic/browser-services';
import PerformanceConsoleLog from './PerformanceConsoleLog';

export class PerformanceUtilService {
  itemKeyPrefix = 'c__perf__';

  newRelicPageActionService = getService(NewRelicPageActionService);

  clearPerfTiming() {
    const performanceObj = window.performance;
    if (!performanceObj) return;
    performanceObj.clearResourceTimings();
    performanceObj.clearMarks();
    performanceObj.clearMeasures();
  }

  getSlowestEntries(maxReportSlowestCount, startTimeStamp) {
    const entries = this.getPFEResources();
    const reportSlowestCount =
      entries.length > maxReportSlowestCount
        ? maxReportSlowestCount
        : entries.length;
    return entries
      .filter(item => 'startTime' in item && item.startTime >= startTimeStamp)
      .sort((a, b) => b.duration - a.duration)
      .slice(0, reportSlowestCount);
  }

  getLogItemKey(originKey) {
    return this.itemKeyPrefix + originKey;
  }

  getNRItemFromEntry(item, moduleName, reportId) {
    const logKey = this.getLogItemKey.bind(this);
    return {
      [logKey('moduleName')]: moduleName,
      [logKey('timeOrigin')]: window.performance.timeOrigin,
      [logKey('reportId')]: reportId,
      [logKey('startTime')]: item.startTime,
      [logKey('duration')]: item.duration,
      [logKey('name')]: item.name,
      [logKey('redirectStart')]: item.redirectStart,
      [logKey('redirectEnd')]: item.redirectEnd,
      [logKey('fetchStart')]: item.fetchStart,
      [logKey('firstInterimResponseStart')]: item.firstInterimResponseStart,
      [logKey('domainLookupStart')]: item.domainLookupStart,
      [logKey('domainLookupEnd')]: item.domainLookupEnd,
      [logKey('connectStart')]: item.connectStart,
      [logKey('connectEnd')]: item.connectEnd,
      [logKey('secureConnectionStart')]: item.secureConnectionStart,
      [logKey('requestStart')]: item.requestStart,
      [logKey('responseStart')]: item.responseStart,
      [logKey('responseEnd')]: item.responseEnd,
      [logKey('transferSize')]: item.transferSize,
      [logKey('encodedBodySize')]: item.encodedBodySize,
      [logKey('decodedBodySize')]: item.decodedBodySize,
      [logKey('nextHopProtocol')]: item.nextHopProtocol,
      [logKey('workerStart')]: item.workerStart,
      [logKey('initiatorType')]: item.initiatorType,
      [logKey('redirectCount')]: item.redirectCount,
      [logKey('entryType')]: item.entryType,
      [logKey('deliveryType')]: item.deliveryType,
      [logKey('renderBlockingStatus')]: item.renderBlockingStatus,
    };
  }

  logEntriesIntoNR(
    pageActionName,
    moduleName,
    entries,
    reportId,
    startTimeStamp,
    endTimeStamp,
  ) {
    const itemDetails = [];
    entries.forEach(item => {
      const nrItem = this.getNRItemFromEntry(item, moduleName, reportId);
      nrItem[this.getLogItemKey('actionStartTime')] = startTimeStamp;
      nrItem[this.getLogItemKey('actionEndTime')] = endTimeStamp;
      itemDetails.push(nrItem);

      this.newRelicPageActionService.addPageAction(pageActionName, nrItem);
    });
    PerformanceConsoleLog.logInfo(
      'nrLogInfo items ',
      pageActionName,
      moduleName,
      itemDetails,
      reportId,
    );
  }

  filterEntries(entries, filterParams) {
    if (filterParams?.entryNameRegex) {
      entries = entries.filter(item =>
        filterParams.entryNameRegex?.test(item.name),
      );
    }
    if (filterParams?.entryType) {
      filterParams.entryType = filterParams.entryType.toUpperCase();
      entries = entries.filter(
        item =>
          item.entryType &&
          item.entryType.toUpperCase() === filterParams.entryType,
      );
    }
    return entries;
  }

  getFirstOrDefault(entries, filterParams) {
    const filteredEntries = this.filterEntries(entries, filterParams);
    return filteredEntries.length > 0 ? filteredEntries[0] : null;
  }

  getEntriesByRegex(entries, entryRegexs) {
    return entryRegexs.map(fileRegexItem => {
      const entryType = fileRegexItem.entryType
        ? fileRegexItem.entryType
        : 'resource';
      const entry = this.getFirstOrDefault(entries, {
        entryNameRegex: fileRegexItem.entryRegex,
        entryType,
      });

      const typedEntryRegexItem = { ...fileRegexItem, entry };
      return typedEntryRegexItem;
    });
  }

  isJSFile(fileName) {
    return fileName.split('?')[0].endsWith('.js');
  }

  isBoolean(value) {
    return typeof value === 'boolean' || value instanceof Boolean;
  }

  isArray(value) {
    return (
      Array.isArray(value) ||
      value instanceof Array ||
      Object.prototype.toString.call(value) === '[object Array]'
    );
  }

  defaultNetworkEntryRegexs = [];

  defaultRecordEntryRegexs = [];

  setDefaultNetworkEntryRegexs(entryRegexs) {
    this.defaultNetworkEntryRegexs = entryRegexs;
  }

  setDefaultRecordEntryRegexs(entryRegexs) {
    this.defaultRecordEntryRegexs = entryRegexs;
  }

  getPFEResources() {
    const performanceObj = window.performance;
    const entries = performanceObj.getEntriesByType('resource');
    if (window.pfeResources) {
      const pfeResources = window.pfeResources.getArray();
      let allEntries = entries.concat(pfeResources);
      allEntries = allEntries.filter(
        (entry, index, self) =>
          index ===
          self.findIndex(
            e =>
              e.name === entry.name &&
              e.entryType === entry.entryType &&
              e.startTime === entry.startTime,
          ),
      );
      // PerformanceConsoleLog.logInfo('getPFEResources pfeResources', entries, pfeResources, allEntries);
      return allEntries;
    }
    return entries;
  }

  hasReportData = false;

  logPerfInfoIntoNR(
    recordEntriesRegexs,
    isInitialLoad,
    pageName,
    startTimeStamp,
    endTimeStamp,
    extraInfo,
  ) {
    try {
      if (isInitialLoad) {
        if (this.hasReportData) {
          PerformanceConsoleLog.logWarn(
            'Page Monitor data has been reported, skip IPL',
          );
          return;
        }
      }
      this.hasReportData = true;
      const performanceObj = window.performance;
      let entries = this.getPFEResources();
      entries = entries.filter(
        item => 'startTime' in item && item.startTime >= startTimeStamp,
      );

      const reportId = performance.now().toString() + Math.random().toString();
      const nrLogInfo = {};
      const maxReportSlowestCount = 10;

      const slowestEntries = this.getSlowestEntries(
        maxReportSlowestCount,
        startTimeStamp,
      );

      let networkEntries = [];
      if (isInitialLoad) {
        networkEntries = this.getEntriesByRegex(
          entries,
          this.defaultNetworkEntryRegexs,
        );
        this.defaultRecordEntryRegexs.forEach(item =>
          recordEntriesRegexs.push(item),
        );
      }

      if (recordEntriesRegexs) {
        const recordEntries = this.getEntriesByRegex(
          entries,
          recordEntriesRegexs,
        );
        networkEntries.forEach(item => recordEntries.push(item));
        recordEntries.forEach(recordEntry => {
          const { entryName, entry } = recordEntry;
          if (entry) {
            nrLogInfo[this.getLogItemKey(`${entryName}StartTime`)] =
              entry.startTime;
            nrLogInfo[this.getLogItemKey(`${entryName}EndTime`)] =
              entry.responseEnd;
            if (recordEntry.isRenderEntry) {
              nrLogInfo[this.getLogItemKey('renderEntryStartTime')] =
                entry.startTime;
              nrLogInfo[this.getLogItemKey('renderEntryEndTime')] =
                entry.responseEnd;
            }
          }
        });
      }

      const jsFileEntries = entries.filter(item => this.isJSFile(item.name));
      const duration = endTimeStamp - startTimeStamp;
      nrLogInfo[this.getLogItemKey('actionStartTime')] = startTimeStamp;
      nrLogInfo[this.getLogItemKey('actionEndTime')] = endTimeStamp;
      nrLogInfo[this.getLogItemKey('moduleEndTime')] = duration;
      nrLogInfo[this.getLogItemKey('duration')] = duration;
      nrLogInfo[this.getLogItemKey('moduleName')] = pageName;
      nrLogInfo[this.getLogItemKey('pageName')] = pageName;
      nrLogInfo[this.getLogItemKey('timeOrigin')] = performanceObj.timeOrigin;
      nrLogInfo[this.getLogItemKey('reportId')] = reportId;
      nrLogInfo[this.getLogItemKey('timeZone')] =
        Intl.DateTimeFormat().resolvedOptions().timeZone;
      nrLogInfo[this.getLogItemKey('totalXHRRequestCount')] = entries.filter(
        item => item.initiatorType?.toLowerCase() === 'xmlhttprequest',
      ).length;
      nrLogInfo[this.getLogItemKey('totalJsFileSize')] = jsFileEntries.reduce(
        (total, item) => total + (item.transferSize || 0),
        0,
      );
      nrLogInfo[this.getLogItemKey('totalJsFileCount')] = jsFileEntries.length;
      nrLogInfo[this.getLogItemKey('cachedJsFileCount')] = jsFileEntries.filter(
        item => item.transferSize === 0,
      ).length;
      nrLogInfo[this.getLogItemKey('notCachedJsFileCount')] =
        jsFileEntries.filter(item => item.transferSize !== 0).length;
      nrLogInfo[this.getLogItemKey('locationHref')] = window.location.href;

      if (extraInfo) {
        Object.keys(extraInfo).forEach(key => {
          nrLogInfo[this.getLogItemKey(key)] = extraInfo[key];
        });
      }

      if (isInitialLoad) {
        networkEntries.forEach(item => {
          const { entryName, entry } = item;
          if (!entry) return;
          nrLogInfo[this.getLogItemKey(`${entryName}DomainLookup`)] =
            (entry?.domainLookupEnd ?? 0) - (entry?.domainLookupStart ?? 0);
          nrLogInfo[this.getLogItemKey(`${entryName}ConnectionSpeed`)] =
            (entry.connectEnd ?? 0) - (entry.connectStart ?? 0);
          nrLogInfo[this.getLogItemKey(`${entryName}TTFB`)] =
            (entry.responseStart ?? 0) - (entry.requestStart ?? 0);
          nrLogInfo[this.getLogItemKey(`${entryName}Download`)] =
            (entry.responseEnd ?? 0) - (entry.responseStart ?? 0);
        });

        this.logEntriesIntoNR(
          'reportPerfInfoItem',
          pageName,
          slowestEntries,
          reportId,
          startTimeStamp,
          endTimeStamp,
        );
        this.newRelicPageActionService.addPageAction(
          'reportPerfInfo',
          nrLogInfo,
        );
        PerformanceConsoleLog.logWarn('initialLoad nrLogInfo base ', nrLogInfo);
      } else {
        this.logEntriesIntoNR(
          'reportPerfInfoItemRouteChange',
          pageName,
          slowestEntries,
          reportId,
          startTimeStamp,
          endTimeStamp,
        );
        this.newRelicPageActionService.addPageAction(
          'reportPerfInfoRouteChange',
          nrLogInfo,
        );
        PerformanceConsoleLog.logWarn('routechange nrLogInfo base ', nrLogInfo);
      }
    } catch (e) {
      PerformanceConsoleLog.logError('logPerfInfoIntoNR error', e);
    }
  }
}
